import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Brush,
  Bar,
  Cell,
  LabelList,
  Rectangle,
  Legend,
  BarChart as BarChartRecharts,
} from 'recharts';
import { SurveyWithinTimeFrame } from '../../api/queries';
import { DateUtils } from '../../utils/DateUtils';
import { AverageScoreChartTooltip } from '../AverageScoreChartTooltip/AverageScoreChartTooltip';
import { chartAxisProps, chartGridProps, formatChartValue, yAxisTicks } from './chartStyle';

type BarChartProps = {
  disabled?: boolean;
  data: SurveyWithinTimeFrame[];
  /** Kompakt: verdien over hver stolpe, uten forklaring og zoom */
  variant?: 'default' | 'compact';
  height?: number;
  /** Farge per stolpe ut fra verdien, i stedet for aksentfargen */
  colorForScore?: (score: number) => string;
};

export const BarChart = ({
  data,
  disabled = false,
  variant = 'default',
  height,
  colorForScore,
}: BarChartProps) => {
  const { t } = useTranslation();
  const isCompact = variant === 'compact';

  const dataWithFormattedDate = data.map((item) => ({
    ...item,
    shortDate: DateUtils.getShortDateFromDate(item.date),
  }));

  const color = disabled ? 'var(--ds-color-neutral-text-subtle)' : 'var(--ds-color-accent-base-default)';

  return (
    <ResponsiveContainer width="100%" height={height ?? (isCompact ? 260 : 450)}>
      <BarChartRecharts
        accessibilityLayer
        data={dataWithFormattedDate}
        margin={{ top: isCompact ? 28 : 5, right: isCompact ? 0 : 30, left: isCompact ? -24 : 0, bottom: 5 }}
      >
        <CartesianGrid {...chartGridProps} />
        <XAxis dataKey="shortDate" dy={8} height={isCompact ? 36 : 60} {...chartAxisProps} />
        <YAxis domain={[0, 5]} ticks={yAxisTicks} dx={-4} {...chartAxisProps} />
        <Tooltip
          aria-describedby="customTooltip"
          cursor={{ fill: 'var(--ds-color-accent-background-tinted)' }}
          content={(props) => (
            <AverageScoreChartTooltip props={props} active={props.active} />
          )}
        />
        {!isCompact && <Legend verticalAlign="top" height={36} />}
        <Bar
          name={t('chart.averageScore')}
          dataKey="averageScore"
          fill={color}
          radius={[6, 6, 0, 0]}
          maxBarSize={120}
          activeBar={colorForScore ? undefined : <Rectangle fill="var(--ds-color-accent-base-hover)" />}
        >
          {colorForScore &&
            dataWithFormattedDate.map((item) => (
              <Cell key={`${item.title}-${String(item.date)}`} fill={colorForScore(item.averageScore)} />
            ))}
          {isCompact && (
            <LabelList
              dataKey="averageScore"
              position="top"
              offset={12}
              formatter={formatChartValue}
              fill="var(--ds-color-neutral-text-default)"
              fontSize={14}
              fontWeight={500}
            />
          )}
        </Bar>
        {!isCompact && (
          <Brush
            alwaysShowText
            dataKey={'shortDate'}
            dy={8}
            fill="var(--ds-color-accent-surface-tinted)"
            stroke="var(--ds-color-accent-base-default)"
          />
        )}
      </BarChartRecharts>
    </ResponsiveContainer>
  );
};
